import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users, Plus, ShieldCheck, Trash2, Edit2, Package } from 'lucide-react';
import { useAdminUsers, useCustomerUsers, useAddCustomerUser, useUpdateCustomerUser, useRemoveCustomerUser, useLicenses } from '../../hooks/useAdminData';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuthStore } from '../../stores/auth.store';
import { TrustedDevicesPanel } from '../../components/admin/TrustedDevicesPanel';
import type { CustomerUser, Product } from '../../types';

export function UsersAdmin() {
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('customerId');

  const { isGlobalAdmin, isUserAdmin, currentCustomerId } = usePermissions();
  const { customer } = useAuthStore();

  // Use the customerId from URL or fall back to current customer
  const targetCustomerId = customerId || currentCustomerId;

  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<CustomerUser | null>(null);
  const [newBrukernavn, setNewBrukernavn] = useState('');
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);

  // Queries - only fetch admin/license data for GlobalAdmin
  const { data: adminUsersData, isLoading: loadingGlobalUsers } = useAdminUsers(isGlobalAdmin);
  const { data: customerUsersData, isLoading: loadingCustomerUsers } = useCustomerUsers(targetCustomerId || '');
  const { data: licensesData } = useLicenses(isGlobalAdmin ? (targetCustomerId || undefined) : undefined);

  // Get products available to this customer (products they have licenses for)
  const availableProducts: Product[] = (licensesData?.licenses || [])
    .map(l => l.product)
    .filter((p): p is Product => p !== undefined)
    .filter((p, i, arr) => arr.findIndex(x => x.id === p.id) === i); // dedupe

  // Mutations
  const addUser = useAddCustomerUser(targetCustomerId || '');
  const updateUser = useUpdateCustomerUser(targetCustomerId || '');
  const removeUser = useRemoveCustomerUser(targetCustomerId || '');

  const adminUsers = adminUsersData?.users ?? [];
  const customerUsers = customerUsersData?.customerUsers ?? [];

  const handleAddUser = async () => {
    if (!newBrukernavn.trim() || !targetCustomerId) return;
    try {
      await addUser.mutateAsync({
        username: newBrukernavn,
        firstName: newFirstName || undefined,
        lastName: newLastName || undefined,
        isAdmin: newIsAdmin,
        productIds: [],
      });
      setNewBrukernavn('');
      setNewFirstName('');
      setNewLastName('');
      setNewIsAdmin(false);
      setShowAddUserModal(false);
    } catch (error) {
      console.error('Failed to add user:', error);
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser || !targetCustomerId) return;
    try {
      await updateUser.mutateAsync({
        userId: editingUser.userId,
        request: {
          isAdmin: newIsAdmin,
          productIds: selectedProductIds,
        },
      });
      setEditingUser(null);
      setSelectedProductIds([]);
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!targetCustomerId || !confirm('Er du sikker pa at du vil fjerne denne brukeren?')) return;
    try {
      await removeUser.mutateAsync(userId);
    } catch (error) {
      console.error('Failed to remove user:', error);
    }
  };

  const startEditing = (user: CustomerUser) => {
    setEditingUser(user);
    setNewIsAdmin(user.isAdmin);
    setSelectedProductIds(user.products.map(p => p.id));
  };

  // Global admin view - show all system users
  if (isGlobalAdmin || isUserAdmin) {
    return (
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-800">Brukeradministrasjon</h1>
          <p className="text-slate-600 mt-1">Administrer systembrukere og roller</p>
        </div>

        {/* Global Users Section */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
          <div className="p-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-800">Systembrukere</h2>
            <p className="text-sm text-slate-500">Alle registrerte brukere i systemet</p>
          </div>
          {loadingGlobalUsers ? (
            <div className="p-8 text-center text-slate-500">Laster brukere...</div>
          ) : adminUsers.length === 0 ? (
            <div className="p-8 text-center text-slate-500">Ingen brukere funnet</div>
          ) : (
            <div className="divide-y divide-slate-200">
              {adminUsers.map((user) => (
                <div key={user.email} className="p-4 flex items-center justify-between hover:bg-slate-50">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                      <Users size={20} className="text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-800">{user.email || user.userName}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {user.roles.map((role) => (
                          <span
                            key={role}
                            className={`text-xs px-2 py-0.5 rounded-full ${
                              role === 'GlobalAdmin'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-blue-100 text-blue-700'
                            }`}
                          >
                            {role}
                          </span>
                        ))}
                        {user.roles.length === 0 && (
                          <span className="text-xs text-slate-400">Ingen roller</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 text-sm text-slate-500">
                    <TrustedDevicesPanel user={user} />
                    {user.emailConfirmed ? (
                      <span className="text-green-600">E-post verifisert</span>
                    ) : (
                      <span className="text-orange-600">E-post ikke verifisert</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Kundebrukere Section */}
        <CustomerUsersSection
          customerId={targetCustomerId || ''}
          customerUsers={customerUsers}
          isLoading={loadingCustomerUsers}
          onAddUser={() => setShowAddUserModal(true)}
          onEditUser={startEditing}
          onRemoveUser={handleRemoveUser}
        />

        {/* Legg til bruker Modal */}
        {showAddUserModal && (
          <AddUserModal
            username={newBrukernavn}
            firstName={newFirstName}
            lastName={newLastName}
            isAdmin={newIsAdmin}
            onBrukernavnChange={setNewBrukernavn}
            onFirstNameChange={setNewFirstName}
            onLastNameChange={setNewLastName}
            onIsAdminChange={setNewIsAdmin}
            onSubmit={handleAddUser}
            onAvbryt={() => setShowAddUserModal(false)}
            isPending={addUser.isPending}
          />
        )}

        {/* Rediger bruker Modal */}
        {editingUser && (
          <EditUserModal
            user={editingUser}
            isAdmin={newIsAdmin}
            onIsAdminChange={setNewIsAdmin}
            availableProducts={availableProducts}
            selectedProductIds={selectedProductIds}
            onProductToggle={(productId) => {
              setSelectedProductIds(prev =>
                prev.includes(productId)
                  ? prev.filter(id => id !== productId)
                  : [...prev, productId]
              );
            }}
            onSubmit={handleUpdateUser}
            onAvbryt={() => { setEditingUser(null); setSelectedProductIds([]); }}
            isPending={updateUser.isPending}
          />
        )}
      </div>
    );
  }

  // Customer admin view - only show customer users
  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Brukerhandtering</h1>
          <p className="text-slate-600 mt-1">
            Administrer brukere for {customer?.name}
          </p>
        </div>
        <button
          onClick={() => setShowAddUserModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={20} />
          Legg til bruker
        </button>
      </div>

      <CustomerUsersSection
        customerId={targetCustomerId || ''}
        customerUsers={customerUsers}
        isLoading={loadingCustomerUsers}
        onAddUser={() => setShowAddUserModal(true)}
        onEditUser={startEditing}
        onRemoveUser={handleRemoveUser}
        showHeader={false}
      />

      {/* Legg til bruker Modal */}
      {showAddUserModal && (
        <AddUserModal
          username={newBrukernavn}
          firstName={newFirstName}
          lastName={newLastName}
          isAdmin={newIsAdmin}
          onBrukernavnChange={setNewBrukernavn}
          onFirstNameChange={setNewFirstName}
          onLastNameChange={setNewLastName}
          onIsAdminChange={setNewIsAdmin}
          onSubmit={handleAddUser}
          onAvbryt={() => setShowAddUserModal(false)}
          isPending={addUser.isPending}
        />
      )}

      {/* Rediger bruker Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          isAdmin={newIsAdmin}
          onIsAdminChange={setNewIsAdmin}
          availableProducts={availableProducts}
          selectedProductIds={selectedProductIds}
          onProductToggle={(productId) => {
            setSelectedProductIds(prev =>
              prev.includes(productId)
                ? prev.filter(id => id !== productId)
                : [...prev, productId]
            );
          }}
          onSubmit={handleUpdateUser}
          onAvbryt={() => { setEditingUser(null); setSelectedProductIds([]); }}
          isPending={updateUser.isPending}
        />
      )}
    </div>
  );
}

// Kundebrukere Section Component
function CustomerUsersSection({
  customerUsers,
  isLoading,
  onAddUser,
  onEditUser,
  onRemoveUser,
  showHeader = true,
}: {
  customerId: string;
  customerUsers: CustomerUser[];
  isLoading: boolean;
  onAddUser: () => void;
  onEditUser: (user: CustomerUser) => void;
  onRemoveUser: (userId: string) => void;
  showHeader?: boolean;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200">
      {showHeader && (
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Kundebrukere</h2>
            <p className="text-sm text-slate-500">Brukere tilknyttet denne kunden</p>
          </div>
          <button
            onClick={onAddUser}
            className="flex items-center gap-2 bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <Plus size={16} />
            Legg til bruker
          </button>
        </div>
      )}
      {isLoading ? (
        <div className="p-8 text-center text-slate-500">Laster brukere...</div>
      ) : customerUsers.length === 0 ? (
        <div className="p-8 text-center text-slate-500">Ingen brukere tilknyttet denne kunden</div>
      ) : (
        <div className="divide-y divide-slate-200">
          {customerUsers.map((user) => (
            <div key={user.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                  {user.isAdmin ? (
                    <ShieldCheck size={20} className="text-purple-600" />
                  ) : (
                    <Users size={20} className="text-slate-600" />
                  )}
                </div>
                <div>
                  <h3 className="font-medium text-slate-800">{user.username}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    {user.isAdmin && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                        Programadmin
                      </span>
                    )}
                    {user.products.length > 0 && (
                      <span className="text-xs text-slate-500">
                        {user.products.length} produkttilgang
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onEditUser(user)}
                  className="p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => onRemoveUser(user.userId)}
                  className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Legg til bruker Modal Component
function AddUserModal({
  username,
  firstName,
  lastName,
  isAdmin,
  onBrukernavnChange,
  onFirstNameChange,
  onLastNameChange,
  onIsAdminChange,
  onSubmit,
  onAvbryt,
  isPending,
}: {
  username: string;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
  onBrukernavnChange: (value: string) => void;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onIsAdminChange: (value: boolean) => void;
  onSubmit: () => void;
  onAvbryt: () => void;
  isPending: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
        <h2 className="text-xl font-bold text-slate-800 mb-4">Legg til bruker</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              E-post *
            </label>
            <input
              type="email"
              value={username}
              onChange={(e) => onBrukernavnChange(e.target.value)}
              placeholder="bruker@eksempel.no"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Fornavn
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => onFirstNameChange(e.target.value)}
                placeholder="Ola"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Etternavn
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => onLastNameChange(e.target.value)}
                placeholder="Nordmann"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isAdmin"
              checked={isAdmin}
              onChange={(e) => onIsAdminChange(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="isAdmin" className="text-sm text-slate-700">
              Gi programadmin-tilgang
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onAvbryt} className="px-4 py-2 text-slate-600 hover:text-slate-800">
            Avbryt
          </button>
          <button
            onClick={onSubmit}
            disabled={isPending || !username.trim()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isPending ? 'Legger til...' : 'Legg til bruker'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Rediger bruker Modal Component
function EditUserModal({
  user,
  isAdmin,
  onIsAdminChange,
  availableProducts,
  selectedProductIds,
  onProductToggle,
  onSubmit,
  onAvbryt,
  isPending,
}: {
  user: CustomerUser;
  isAdmin: boolean;
  onIsAdminChange: (value: boolean) => void;
  availableProducts: Product[];
  selectedProductIds: string[];
  onProductToggle: (productId: string) => void;
  onSubmit: () => void;
  onAvbryt: () => void;
  isPending: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
        <h2 className="text-xl font-bold text-slate-800 mb-4">Rediger bruker</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Brukernavn
            </label>
            <input
              type="text"
              value={user.username}
              disabled
              className="w-full px-4 py-2 border border-slate-300 rounded-lg bg-slate-100 text-slate-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="editIsAdmin"
              checked={isAdmin}
              onChange={(e) => onIsAdminChange(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="editIsAdmin" className="text-sm text-slate-700">
              Programadmin-tilgang (alle produkter)
            </label>
          </div>

          {/* Produkttilgang Section */}
          {!isAdmin && availableProducts.length > 0 && (
            <div className="border-t border-slate-200 pt-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Produkttilgang
              </label>
              <p className="text-xs text-slate-500 mb-3">
                Velg hvilke produkter denne brukeren har tilgang til
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {availableProducts.map((product) => (
                  <label
                    key={product.id}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedProductIds.includes(product.id)}
                      onChange={() => onProductToggle(product.id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <Package size={16} className="text-slate-400" />
                    <span className="text-sm text-slate-700">{product.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {isAdmin && (
            <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
              Programadminer har tilgang til alle lisensierte produkter
            </p>
          )}
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onAvbryt} className="px-4 py-2 text-slate-600 hover:text-slate-800">
            Avbryt
          </button>
          <button
            onClick={onSubmit}
            disabled={isPending}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isPending ? 'Lagrer...' : 'Lagre'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UsersAdmin;
